<template>
  <div class="modal no-overflow show">
    <!--START: Header-->
    <ModalHeader :title="'Connect ' + provider.name" @closeModal="$emit('close')"></ModalHeader>
    <!--END: Header-->

    <!--START: Body-->
    <div class="modal-body">
      <div class="scroll-body">
        <!--START: Provider Info-->
        <div class="provider-info">
          <img :src="logoUrl" class="provider-logo" :alt="provider.name" />
          <div class="provider-details">
            <h4>{{ provider.name }}</h4>
            <p>{{ provider.description }}</p>
          </div>
        </div>
        <!--END: Provider Info-->

        <!--START: Form Fields-->
        <div v-if="formFields.length > 0" class="connect-fields">
          <template v-for="field in formFields">
            <div v-if="field.type === 'section'" :key="field.key" class="section-header">
              <h5>{{ field.label }}</h5>
              <p v-if="field.description">{{ field.description }}</p>
            </div>
            <FormBuilder
              v-else
              :key="field.key"
              :fields="singleBuilderField(field)"
            ></FormBuilder>
          </template>
        </div>
        <!--END: Form Fields-->

        <!--START: Error-->
        <p v-if="error" class="error-text">{{ error }}</p>
        <!--END: Error-->

        <!--START: Action-->
        <button
          class="btn btn-small btn-primary"
          :disabled="submitting"
          @click="handleConnect"
        >
          <span>{{ isOAuth ? "Continue with OAuth" : "Connect" }}</span>
        </button>
        <!--END: Action-->
      </div>
    </div>
    <!--END: Body-->

    <!--START: Loader-->
    <LineLoader :show="submitting" class="bottom"></LineLoader>
    <!--END: Loader-->
  </div>
</template>

<script>
import ModalHeader from "@/components/modals/ModalHeader";
import FormBuilder from "@/components/form/FormBuilder";
import LineLoader from "@/components/loaders/LineLoader";
import { IntegrationService } from "@/services";

export default {
  name: "ConnectModal",
  components: {
    ModalHeader,
    FormBuilder,
    LineLoader,
  },
  props: {
    provider: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      submitting: false,
      error: "",
      fieldRefs: {},
    };
  },
  computed: {
    isOAuth() {
      return this.provider.authType === "oauth2";
    },

    formFields() {
      if (this.isOAuth) {
        const dynamicFields = this.provider.oauth?.dynamicFields || [];
        return (this.provider.connectFields || []).filter((f) =>
          dynamicFields.includes(f.key),
        );
      }
      return this.provider.connectFields || [];
    },

    // All input fields (excluding section headers)
    inputFields() {
      return this.formFields.filter((f) => f.type !== "section");
    },

    logoUrl() {
      if (!this.provider.logo) return "";
      try {
        return require(`@/assets/images/apps/${this.provider.logo}`);
      } catch (e) {
        return require("@/assets/images/no-image.png");
      }
    },
  },
  methods: {
    singleBuilderField(field) {
      if (!this.fieldRefs[field.key]) {
        this.fieldRefs[field.key] = {
          type: field.type || "text",
          title: field.label,
          placeholder: field.placeholder || "",
          required: field.required || false,
          hasError: false,
          isSingleRow: true,
          value: "",
        };
      }
      return { [field.key]: this.fieldRefs[field.key] };
    },

    async handleConnect() {
      this.error = "";

      // Validate required fields (skip sections)
      for (const field of this.inputFields) {
        const ref = this.fieldRefs[field.key];
        if (field.required && !ref?.value) {
          this.error = `${field.label} is required`;
          return;
        }
      }

      this.submitting = true;

      if (this.isOAuth) {
        await this.startOAuth();
      } else {
        await this.connectApiKey();
      }

      this.submitting = false;
    },

    // Extract form values from input fields (skip sections)
    getFormValues() {
      const values = {};
      for (const field of this.inputFields) {
        values[field.key] = this.fieldRefs[field.key]?.value || "";
      }
      return values;
    },

    async startOAuth() {
      const response = await IntegrationService.OAuthStart({
        provider: this.provider.key,
        metadata: { ...this.getFormValues() },
      });

      if (response?.hasError) {
        this.error = response.message || "Failed to start OAuth";
        return;
      }

      this.openOAuthPopup(response.data);
    },

    openOAuthPopup(url) {
      const width = 500;
      const height = 650;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;

      const popup = window.open(
        url,
        "oauth_popup",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
      );

      const handler = async (event) => {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type !== "integration_oauth") return;

        window.removeEventListener("message", handler);

        if (data.success) {
          this.$emit("connected");
        } else {
          this.error = `Connection failed: ${data.error || "unknown"}`;
        }
      };

      window.addEventListener("message", handler);

      const pollTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener("message", handler);
        }
      }, 500);
    },

    async connectApiKey() {
      const response = await IntegrationService.Connect({
        provider: this.provider.key,
        credentials: { ...this.getFormValues() },
        metadata: {},
      });

      if (response?.hasError) {
        this.error = response.message || "Failed to connect";
        return;
      }

      this.$emit("connected");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.provider-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid $borderColor;
}

.provider-logo {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  border: 1px solid lighten($lightGreyColor, 20%);
  object-fit: contain;
  flex-shrink: 0;
}

.provider-details {
  h4 {
    font-size: $smallFontSize;
    font-weight: $boldFontWeight;
    margin-bottom: 0.1rem;
  }

  p {
    font-size: $smallerFontSize;
    color: $paragraphColor;
    line-height: 1.4;
    margin-bottom: 0;
  }
}

.section-header {
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid $borderColor;

  h5 {
    font-size: $smallFontSize;
    font-weight: $boldFontWeight;
    color: $titleColor;
    margin-bottom: 0.2rem;
  }

  p {
    font-size: $smallestFontSize;
    color: $paragraphColor;
    line-height: 1.4;
    margin-bottom: 0;
  }
}

.error-text {
  font-size: $smallestFontSize;
  color: $redColor;
  margin-bottom: 0.75rem;
}

.btn-primary {
  float: right;
  margin-top: 0.5rem;
}

::v-deep {
  .field-item {
    margin-bottom: 1rem;

    .single-row {
      align-items: center;
    }
  }
}
</style>
